// src/components/FileUploadStats.tsx

import React from 'react'
import { Card, Row, Col, Badge } from 'react-bootstrap'
import { useAppSelector } from '../store/hooks'
import { selectAggregateStats } from '../store/slices/datasetSlice'
import styled from 'styled-components'

const StyledCard = styled(Card)`
  background: #212529;
  border: none;
  border-radius: 0.5rem;
  overflow: hidden;
`

const StatRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`

const StatLabel = styled.span`
  width: 140px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`

const StatValue = styled.span`
  color: #ffffff;
  font-weight: 500;
`

interface FileUploadStatsProps {
  numFilesSelected: number
  labelsFileName: string
  datasetSource: string
}

const FileUploadStats: React.FC<FileUploadStatsProps> = ({
  numFilesSelected,
  labelsFileName,
  datasetSource,
}) => {
  const stats = useAppSelector(selectAggregateStats)

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const statItems = [
    { label: 'Labels File', value: labelsFileName },
    { label: 'Dataset', value: datasetSource },
    { label: 'Files Selected', value: numFilesSelected },
    {
      label: 'Succeeded',
      value: stats.total_files_succeeded,
      variant: 'success',
    },
    {
      label: 'Duplicates',
      value: stats.total_files_duplicate,
      variant: 'warning',
    },
    { label: 'Failed', value: stats.total_files_failed, variant: 'danger' },
    {
      label: 'Errors',
      value: stats.errors.length,
      variant: stats.errors.length > 0 ? 'danger' : undefined,
    },
    { label: 'Total Images', value: stats.total_files },
    { label: 'Total Size', value: formatBytes(stats.total_bytes) },
  ]

  return (
    <StyledCard>
      <Card.Body className="p-4">
        <h5 className="mb-4 text-light">Upload Summary</h5>
        <Row>
          <Col>
            {statItems.map((item, index) => (
              <StatRow key={index}>
                <StatLabel>{item.label}:</StatLabel>
                {item.variant ? (
                  <Badge
                    bg="transparent"
                    text={item.variant}
                    className={`border border-${item.variant}`}
                  >
                    {item.value}
                  </Badge>
                ) : (
                  <StatValue>{item.value}</StatValue>
                )}
              </StatRow>
            ))}
          </Col>
        </Row>
      </Card.Body>
    </StyledCard>
  )
}

export default FileUploadStats
